/**
 * Page shell.
 *
 * What changed from the first pass, and why:
 *  - The title lived in the sticky header at 18px next to the sidebar toggle,
 *    so every page opened with its most important words wedged into chrome.
 *    The header now carries a breadcrumb trail (orientation) and the page owns
 *    a real title block (identity).
 *  - Content was capped at `max-w-7xl` (1280px) and centred. On a 1512px
 *    screen with a 256px sidebar that left ~100px of dead margin either side
 *    of a table that wanted the room. It now fills the width up to 1600px.
 *  - Sections were 48px apart. That is a landing-page rhythm; a console reads
 *    as one dense surface, so it is 24px.
 */
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import { AppSidebar } from '@/components/app-sidebar';
import { NotificationBell } from '@/components/notification-bell';
import { ThemeToggle } from '@/components/theme-toggle';
import { CommandPalette } from '@/components/command-palette';
import { nav_item_for_path } from '@/components/nav-items';
import { cn } from '@/lib/utils';

export type Crumb = { label: string; to?: string };

export function DashboardLayout({
  title,
  subtitle,
  actions,
  /** Extra trail segments below the module, e.g. the record being viewed. */
  breadcrumbs,
  /** Sub-navigation for a module with several views. */
  tabs,
  /** Summary tiles that belong above the page's main surface. */
  stats,
  /**
   * Avatar / logo for a record page, rendered beside the title.
   *
   * Detail pages otherwise end up repeating the record's name — once as the
   * page title and again in an identity card below it — because the avatar had
   * nowhere else to live.
   */
  avatar,
  /** Status pills that belong beside the title, e.g. Active / Blocked. */
  title_badges,
  /** One line of secondary facts under the subtitle (contact details, ids). */
  meta,
  children,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumbs?: Crumb[];
  tabs?: ReactNode;
  stats?: ReactNode;
  avatar?: ReactNode;
  title_badges?: ReactNode;
  meta?: ReactNode;
  children: ReactNode;
}) {
  const location = useLocation();
  const module = nav_item_for_path(location.pathname);
  // The module blurb is a decent default subtitle, so no page has a bare title.
  const description = subtitle ?? module?.blurb;

  const trail: Crumb[] = [
    ...(module && module.to !== '/' ? [{ label: module.title, to: module.to }] : []),
    ...(breadcrumbs ?? []),
  ];

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="sticky top-0 z-sticky flex h-14 items-center gap-2 border-b border-border bg-card/85 px-3 backdrop-blur-md">
          <SidebarTrigger />
          <Separator orientation="vertical" className="h-5" />

          <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 text-data">
            <Link
              to="/"
              className="shrink-0 font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Console
            </Link>
            {trail.map((crumb, i) => (
              <span key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-1">
                <ChevronRight
                  aria-hidden="true"
                  className="h-3.5 w-3.5 shrink-0 text-muted-foreground/60"
                />
                {crumb.to && i < trail.length - 1 ? (
                  <Link
                    to={crumb.to}
                    className="truncate font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="truncate font-semibold">
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-1">
            {/* A visible affordance for the palette. A keyboard shortcut
                nobody is told about is a feature only its author has. */}
            <PaletteHint />
            <NotificationBell />
            <ThemeToggle />
          </div>
        </header>

        {/*
          min-w-0 on both levels: a flex child defaults to `min-width: auto`, so
          a wide table refused to shrink and stretched this column past the
          viewport — the whole PAGE scrolled sideways at 768px and below,
          dragging the sticky header off-screen with it. With this, wide content
          scrolls inside its own container as intended.
        */}
        <main className="console-canvas min-w-0 flex-1 px-3 py-4 sm:px-4">
          <div className="mx-auto flex min-w-0 max-w-[1600px] flex-col gap-gutter">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-center gap-3">
                {avatar}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="font-heading text-2xl font-bold leading-tight tracking-tight">
                      {title}
                    </h1>
                    {title_badges}
                  </div>
                  {description && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
                  )}
                  {meta && <div className="mt-1.5">{meta}</div>}
                </div>
              </div>
              {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
            </div>

            {tabs}
            {stats}
            {children}
          </div>
        </main>
      </SidebarInset>

      <CommandPalette />
    </SidebarProvider>
  );
}

function PaletteHint() {
  // Dispatching the same event the palette listens for keeps one code path for
  // both the click and the shortcut.
  const open = () =>
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'k', ctrlKey: true, bubbles: true }),
    );

  return (
    <button
      type="button"
      onClick={open}
      className={cn(
        'hidden h-9 items-center gap-2 rounded-control bg-muted/60 px-2.5 text-data text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex',
      )}
      data-testid="palette-trigger"
    >
      <Search className="h-3.5 w-3.5" />
      <span className="hidden md:inline">Search</span>
      <kbd className="rounded border border-border bg-card px-1 py-px text-micro font-semibold">
        ⌘K
      </kbd>
    </button>
  );
}

/**
 * Module sub-navigation. Used where one module has genuinely distinct views
 * (a queue vs. everything, say) rather than as a place to hide filters.
 */
export function PageTabs({
  tabs,
  current,
  on_change,
}: {
  tabs: { value: string; label: string; count?: number }[];
  current: string;
  on_change: (value: string) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Views"
      className="flex w-fit max-w-full gap-0.5 overflow-x-auto rounded-panel bg-muted/60 p-0.5 thin-scroll"
    >
      {tabs.map((tab) => {
        const active = tab.value === current;
        return (
          <button
            key={tab.value}
            role="tab"
            aria-selected={active}
            onClick={() => on_change(tab.value)}
            className={cn(
              'inline-flex shrink-0 items-center gap-1.5 rounded-control px-3 py-1.5 text-data font-semibold transition-colors',
              active
                ? 'bg-card text-foreground shadow-panel'
                : 'text-muted-foreground hover:text-foreground',
            )}
            data-testid={`tab-${tab.value}`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  'tabular rounded-pill px-1.5 text-micro',
                  active ? 'bg-primary/12 text-primary' : 'bg-border/60',
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
